/**
 * Micro adjust — local as-built corrections on the DXF.
 *
 * The drawing is the DESIGN; the site is what got built. A row moved two
 * metres during construction shows up as one region of the drawing sitting
 * off its panels while everything else is right — no global transform can
 * fix that, because nothing global is wrong.
 *
 * Selection is SPATIAL, not by layer or feature: Kalia's DXF is 70,415 line
 * features in a single flat layer (~64 fragments per string), so there is no
 * clickable "object" and no layer to filter by. Drag a box around the row
 * that is off; every fragment whose centre falls inside moves together.
 *
 * The correction is stored as the ZONE itself (page-space quad + delta), not
 * as a list of feature ids: a Re-read re-parses the DXF and can renumber
 * every feature, which would silently invalidate id-based edits — a zone
 * survives it, stays listable, and can be reset on its own.
 */

import { useEffect } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useMapStore } from '../../stores/mapStore';
import { inverseTransformPoint, transformPoint } from '../../domain/layout/transform';
import { adjustPivot, isIdentityAdjust, applyAdjust, zonePolys } from '../../utils/dxfRegistration';
import { DXF_HL_LINE, DXF_HL_FILL } from './useDxfLayers';
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import type { LayoutTransform } from '../../domain/layout/types';

const ZONE_SRC = 'dxf-zones';
const ZONE_FILL = 'dxf-zones-fill';
const ZONE_LINE = 'dxf-zones-line';
const MIN_BOX_PX = 6;

/** Page → map for drawing the zone outlines back on screen. */
function zoneFeatures(
  zones: Array<{ id: string; poly: Array<[number, number]>; dx: number; dy: number }>,
  tf: LayoutTransform,
  selectedId: string | null,
  macro: (x: number, y: number) => [number, number],
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: zones.map((z) => ({
      type: 'Feature' as const,
      properties: { id: z.id, selected: z.id === selectedId ? 1 : 0 },
      geometry: {
        // MultiPolygon: a zone can hold several parts (Ctrl-clicked elements)
        type: 'MultiPolygon' as const,
        // Draw the zone where its content now sits: its own delta, then the
        // whole-drawing adjust — the same chain the geometry travels.
        coordinates: zonePolys(z).map((poly) => [poly.map(([x, y]) => {
          const [ax, ay] = macro(x + z.dx, y + z.dy);
          return transformPoint(tf, ax, ay);
        })]),
      },
    })),
  };
}

export default function useDxfZones(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
) {
  const zoneOverlayId = useMapStore((s) => s.dxfZoneOverlayId);
  const selectedZoneId = useMapStore((s) => s.dxfSelectedZoneId);

  useEffect(() => {
    const map = mapRef.current;
    if (!isMapReady || !map || !zoneOverlayId) return;

    const ctx = () => {
      const st = useProjectStore.getState();
      const overlay = st.data.overlays.find((o) => o.id === zoneOverlayId);
      const dxf = overlay?.dxf;
      const reg = dxf?.registration;
      if (!dxf || reg?.kind !== 'layout') return null;
      const layouts = st.data.siteLayouts ?? (st.data.siteLayout ? [st.data.siteLayout] : []);
      const tf = layouts.find((l) => l.id === reg.layoutId)?.transform;
      if (!tf) return null;
      return { st, overlay: overlay!, dxf, reg, tf };
    };

    /** lng/lat → RAW page space (undoing the macro adjust so a zone stays
     *  attached to the same drawing content when the drawing is nudged). */
    const toPageLngLat = (lng: number, lat: number): [number, number] | null => {
      const c = ctx();
      if (!c) return null;
      const [px, py] = inverseTransformPoint(c.tf, lng, lat);
      if (isIdentityAdjust(c.dxf.adjust)) return [px, py];
      // Invert the macro adjust: page' = pivot + S·R·(page − pivot) + T
      const a = c.dxf.adjust!;
      const pivot = adjustPivot(c.dxf.extents, c.reg.matrix);
      const rad = (-a.rotDeg * Math.PI) / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const ux = (px - a.dx - pivot[0]) / (a.scale || 1);
      const uy = (py - a.dy - pivot[1]) / (a.scale || 1);
      return [pivot[0] + ux * cos - uy * sin, pivot[1] + ux * sin + uy * cos];
    };

    /** screen point → RAW page space. */
    const toPage = (pt: { x: number; y: number }): [number, number] | null => {
      const ll = map.unproject([pt.x, pt.y]);
      return toPageLngLat(ll.lng, ll.lat);
    };

    /** Live preview while a zone is being dragged (no data rebuild of the
     *  70k-feature content until the drag ends — the box moves, the content
     *  lands on release). */
    let preview: { id: string; dx: number; dy: number } | null = null;

    const redraw = () => {
      const c = ctx();
      if (!c) return;
      const pivot = adjustPivot(c.dxf.extents, c.reg.matrix);
      const macro = (x: number, y: number): [number, number] =>
        (isIdentityAdjust(c.dxf.adjust) ? [x, y] : applyAdjust(c.dxf.adjust, pivot, x, y));
      let zones = c.dxf.zones ?? [];
      if (preview) {
        const p = preview;
        zones = zones.map((z) => (z.id === p.id ? { ...z, dx: p.dx, dy: p.dy } : z));
      }
      const data = zoneFeatures(zones, c.tf, useMapStore.getState().dxfSelectedZoneId, macro);
      const src = map.getSource(ZONE_SRC) as mapboxgl.GeoJSONSource | undefined;
      if (src) { src.setData(data); return; }
      try {
        map.addSource(ZONE_SRC, { type: 'geojson', data });
        map.addLayer({
          id: ZONE_FILL, type: 'fill', source: ZONE_SRC,
          paint: {
            'fill-color': ['case', ['==', ['get', 'selected'], 1], '#13d6c6', '#ffb020'],
            'fill-opacity': 0.12,
          },
        });
        map.addLayer({
          id: ZONE_LINE, type: 'line', source: ZONE_SRC,
          paint: {
            'line-color': ['case', ['==', ['get', 'selected'], 1], '#13d6c6', '#ffb020'],
            'line-width': ['case', ['==', ['get', 'selected'], 1], 2.5, 1.5],
            'line-dasharray': [2, 1],
          },
        });
      } catch { /* style mid-teardown */ }
    };
    redraw();

    // ── box draw ──
    let start: { x: number; y: number } | null = null;
    let box: HTMLDivElement | null = null;
    let dragZone: { id: string; from: { x: number; y: number }; base: [number, number] } | null = null;
    const container = map.getCanvasContainer();
    const canvasEl = map.getCanvas();

    /**
     * Click-select ONE element: wrap the clicked feature in its own snug
     * zone. Kalia showed why this is the right shape for "single element" —
     * its DXF is 70k fragments in one flat layer, so there is nothing to
     * select by layer, and storing feature ids would not survive a Re-read.
     * A tight crossing-zone around the clicked geometry behaves like an
     * element selection while staying pure zone data.
     *
     * Crossing (not window) deliberately: queryRenderedFeatures returns
     * TILE-CLIPPED geometry, so a long feature's box may cover only part of
     * it — a window zone would then fail to move the very thing you clicked.
     */
    const selectSingleElement = (
      pt: { x: number; y: number },
      c: NonNullable<ReturnType<typeof ctx>>,
      addToSelection = false,
    ) => {
      const layers = (map.getStyle()?.layers ?? [])
        .map((l) => l.id)
        .filter((id) => id.startsWith('dxf-layer-line-') || id.startsWith('dxf-layer-fill-'));
      if (!layers.length) return;
      const hits = map.queryRenderedFeatures([[pt.x - 4, pt.y - 4], [pt.x + 4, pt.y + 4]], { layers });
      if (!hits.length) {
        useMapStore.getState().setDxfSelectedZone(null);
        redraw();
        return;
      }
      const coords: Array<[number, number]> = [];
      const g = hits[0].geometry;
      if (g.type === 'LineString') coords.push(...(g.coordinates as Array<[number, number]>));
      else if (g.type === 'Polygon') coords.push(...(g.coordinates[0] as Array<[number, number]>));
      else if (g.type === 'MultiLineString') for (const l of g.coordinates) coords.push(...(l as Array<[number, number]>));
      if (!coords.length) return;
      const pagePts = coords
        .map(([lng, lat]) => toPageLngLat(lng, lat))
        .filter((p): p is [number, number] => !!p);
      if (!pagePts.length) return;
      const xs = pagePts.map((p) => p[0]), ys = pagePts.map((p) => p[1]);
      const mPerPt = Math.hypot(c.tf.matrix[0], c.tf.matrix[3]) || 1;
      const pad = 0.15 / mPerPt; // ~15 cm of slack, in page units
      const x0 = Math.min(...xs) - pad, x1 = Math.max(...xs) + pad;
      const y0 = Math.min(...ys) - pad, y1 = Math.max(...ys) + pad;
      const part: Array<[number, number]> = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];

      // Ctrl-click: add this element as another PART of the selected zone, so
      // both move together. (Without this the click made a second zone and
      // only the newest one moved — the first went unselected/amber, which is
      // exactly what the owner saw.)
      const selId = useMapStore.getState().dxfSelectedZoneId;
      const existing = selId ? (c.dxf.zones ?? []).find((z) => z.id === selId) : undefined;
      if (addToSelection && existing) {
        const zones = (c.dxf.zones ?? []).map((z) =>
          z.id === existing.id ? { ...z, polys: [...(z.polys ?? []), part] } : z);
        c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones } });
        // Same id stays selected; the edit set rebuilds because the parts
        // changed.
        useMapStore.getState().setDxfSelectedZone(null);
        useMapStore.getState().setDxfSelectedZone(existing.id);
        return;
      }

      const id = `e${(c.dxf.zones?.length ?? 0) + 1}-${Math.round(pt.x)}${Math.round(pt.y)}`;
      useMapStore.getState().setDxfSelectedZone(id); // select first — see above
      const zones = [...(c.dxf.zones ?? []), {
        id, poly: part, dx: 0, dy: 0, mode: 'crossing' as const,
      }];
      c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones } });
    };

    /** Live feedback for the captured elements during a zone drag. */
    const setHighlightTranslate = (dx: number, dy: number) => {
      for (const [id, prop] of [[DXF_HL_LINE, 'line-translate'], [DXF_HL_FILL, 'fill-translate']] as const) {
        try {
          if (map.getLayer(id)) map.setPaintProperty(id, prop, [dx, dy]);
        } catch { /* layer not up yet */ }
      }
    };

    /** Screen-space drag → delta in RAW page units (zones' own frame). */
    const dragDelta = (from: { x: number; y: number }, to: { x: number; y: number }): [number, number] | null => {
      const a = toPage(from), b = toPage(to);
      if (!a || !b) return null;
      return [b[0] - a[0], b[1] - a[1]];
    };

    const onDown = (e: MapMouseEvent) => {
      if (e.originalEvent.button !== 0) return;
      // On an existing zone: select and DRAG it, rather than drawing a new
      // one on top (the owner's first instinct — and the right one).
      const hit = map.queryRenderedFeatures(e.point, { layers: [ZONE_FILL] });
      if (hit.length) {
        const id = String(hit[0].properties?.id ?? '');
        useMapStore.getState().setDxfSelectedZone(id || null);
        const c = ctx();
        const z = c?.dxf.zones?.find((zz) => zz.id === id);
        if (z) {
          dragZone = { id, from: { x: e.point.x, y: e.point.y }, base: [z.dx, z.dy] };
          map.dragPan.disable();
        }
        redraw();
        return;
      }
      start = { x: e.point.x, y: e.point.y };
      map.dragPan.disable();
      box = document.createElement('div');
      box.style.cssText = 'position:absolute;border:1.5px dashed #13d6c6;background:rgba(19,214,198,0.12);pointer-events:none;z-index:5';
      container.appendChild(box);
    };

    const onMove = (e: MapMouseEvent) => {
      if (dragZone) {
        const d = dragDelta(dragZone.from, e.point);
        if (d) {
          preview = { id: dragZone.id, dx: dragZone.base[0] + d[0], dy: dragZone.base[1] + d[1] };
          redraw();
        }
        // The highlighted elements follow the box live via a PAINT
        // translate — no rebuild of the captured geometry mid-drag.
        setHighlightTranslate(e.point.x - dragZone.from.x, e.point.y - dragZone.from.y);
        return;
      }
      if (!start) {
        // Hover feedback: a hand over a zone, crosshair over empty space.
        const over = map.queryRenderedFeatures(e.point, { layers: [ZONE_FILL] }).length > 0;
        canvasEl.style.cursor = over ? 'move' : 'crosshair';
        return;
      }
      if (!box) return;
      const x = Math.min(start.x, e.point.x), y = Math.min(start.y, e.point.y);
      box.style.left = `${x}px`;
      box.style.top = `${y}px`;
      box.style.width = `${Math.abs(e.point.x - start.x)}px`;
      box.style.height = `${Math.abs(e.point.y - start.y)}px`;
    };

    const onUp = (e: MapMouseEvent) => {
      if (dragZone) {
        const dz = dragZone;
        dragZone = null;
        preview = null;
        map.dragPan.enable();
        setHighlightTranslate(0, 0);
        const d = dragDelta(dz.from, e.point);
        const c = ctx();
        if (d && c && (d[0] !== 0 || d[1] !== 0)) {
          const zones = (c.dxf.zones ?? []).map((z) =>
            z.id === dz.id ? { ...z, dx: dz.base[0] + d[0], dy: dz.base[1] + d[1] } : z);
          c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones } });
        }
        redraw();
        return;
      }
      const from = start;
      start = null;
      map.dragPan.enable();
      if (box) { box.remove(); box = null; }
      if (!from) return;
      const c = ctx();
      if (!c) return;
      // A click, not a box: select the single element under the cursor by
      // wrapping it in its own snug zone — so "one element" costs nothing
      // extra in the data model and still survives a Re-read.
      if (Math.abs(e.point.x - from.x) < MIN_BOX_PX && Math.abs(e.point.y - from.y) < MIN_BOX_PX) {
        // Ctrl/⌘ adds the clicked element to the current selection instead of
        // starting a new one.
        selectSingleElement(e.point, c, e.originalEvent.ctrlKey || e.originalEvent.metaKey);
        return;
      }
      if (Math.abs(e.point.x - from.x) < MIN_BOX_PX || Math.abs(e.point.y - from.y) < MIN_BOX_PX) return;
      const corners: Array<{ x: number; y: number }> = [
        { x: from.x, y: from.y }, { x: e.point.x, y: from.y },
        { x: e.point.x, y: e.point.y }, { x: from.x, y: e.point.y },
      ];
      const poly: Array<[number, number]> = [];
      for (const p of corners) {
        const pg = toPage(p);
        if (!pg) return;
        poly.push(pg);
      }
      // AutoCAD's convention: left→right encloses (window), right→left
      // touches (crossing).
      const mode: 'window' | 'crossing' = e.point.x >= from.x ? 'window' : 'crossing';
      const id = `z${(c.dxf.zones?.length ?? 0) + 1}-${Math.round(e.point.x)}${Math.round(e.point.y)}`;
      // SELECT FIRST, then write the zone. The renderer omits the selected
      // zone from the big sources' signature (its content is drawn by the
      // edit source), so this ordering means creating a zone causes no
      // rebuild at all. The other order made the new zone briefly a
      // non-selected one — a full re-encode of every picked layer — and then
      // another when it became selected. That was the multi-second wait.
      useMapStore.getState().setDxfSelectedZone(id);
      const zones = [...(c.dxf.zones ?? []), { id, poly, dx: 0, dy: 0, mode }];
      c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones } });
    };

    // ── nudge the selected zone ──
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        useMapStore.getState().setDxfZoneOverlay(null);
        return;
      }
      const sel = useMapStore.getState().dxfSelectedZoneId;
      if (!sel) return;
      const dirs: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1],
      };
      const d = dirs[ev.key];
      const c = ctx();
      if (!c) return;
      if (d) {
        ev.preventDefault();
        const mPerPt = Math.hypot(c.tf.matrix[0], c.tf.matrix[3]);
        const step = (ev.shiftKey ? 1 : 0.25) / (mPerPt || 1);
        const zones = (c.dxf.zones ?? []).map((z) =>
          z.id === sel ? { ...z, dx: z.dx + d[0] * step, dy: z.dy + d[1] * step } : z);
        c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones } });
        redraw();
      } else if (ev.key === 'Delete' || ev.key === 'Backspace') {
        ev.preventDefault();
        const zones = (c.dxf.zones ?? []).filter((z) => z.id !== sel);
        c.st.updateOverlay(zoneOverlayId, { dxf: { ...c.dxf, zones: zones.length ? zones : undefined } });
        useMapStore.getState().setDxfSelectedZone(null);
        redraw();
      }
    };

    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    window.addEventListener('keydown', onKey);
    const canvas = map.getCanvas();
    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';

    const unsub = useProjectStore.subscribe((s, p) => {
      if (s.data.overlays !== p.data.overlays) redraw();
    });

    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      unsub();
      canvas.style.cursor = prevCursor;
      map.dragPan.enable();
      if (box) box.remove();
      try {
        if (map.getLayer(ZONE_LINE)) map.removeLayer(ZONE_LINE);
        if (map.getLayer(ZONE_FILL)) map.removeLayer(ZONE_FILL);
        if (map.getSource(ZONE_SRC)) map.removeSource(ZONE_SRC);
      } catch { /* tearing down */ }
    };
  }, [isMapReady, mapRef, zoneOverlayId, selectedZoneId]);
}
